/**
 * ui-renderers/transparency.ts
 * Thread-recall banner and transparency popover ("Why did Kili say this?").
 */

import { escapeHtml } from './core';

interface PopoverElement extends HTMLDivElement {
  _anchor?: Element;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal
// ─────────────────────────────────────────────────────────────────────────────

function _toggleTransparencyPopover(anchorBtn: HTMLButtonElement, contextUsed: any) {
  const existing = document.querySelector('.ai-transparency-popover') as PopoverElement | null;
  if (existing) {
    existing.parentNode && existing.parentNode.removeChild(existing);
    if (existing._anchor === anchorBtn) return;
  }

  const popover = document.createElement('div') as PopoverElement;
  popover.className = 'ai-transparency-popover';
  popover._anchor   = anchorBtn;
  popover.setAttribute('role', 'tooltip');

  const lines: string[] = [];
  lines.push('<div class="ai-tp-title">Why Kili said this</div>');

  if (contextUsed.facts?.length) {
    lines.push('<div class="ai-tp-section">');
    lines.push('<div class="ai-tp-section-label">📋 Remembered facts used</div>');
    contextUsed.facts.forEach((id: string) => {
      lines.push('<div class="ai-tp-item">' + escapeHtml(String(id)) + '</div>');
    });
    lines.push('</div>');
  }

  if (contextUsed.recall?.length) {
    lines.push('<div class="ai-tp-section">');
    lines.push('<div class="ai-tp-section-label">💭 Past conversation recalled</div>');
    contextUsed.recall.forEach((id: string) => {
      lines.push('<div class="ai-tp-item">' + escapeHtml(String(id)) + '</div>');
    });
    lines.push('</div>');
  }

  if (contextUsed.style) {
    lines.push('<div class="ai-tp-section">');
    lines.push('<div class="ai-tp-section-label">🎨 Style adaptation</div>');
    lines.push('<div class="ai-tp-item">' + escapeHtml(contextUsed.style) + '</div>');
    lines.push('</div>');
  }

  popover.innerHTML = lines.join('');

  const btnRect = anchorBtn.getBoundingClientRect();
  popover.style.cssText = [
    'position:fixed',
    `bottom:${Math.round(window.innerHeight - btnRect.top + 8)}px`,
    `left:${Math.max(8, Math.round(btnRect.left - 180))}px`,
    'z-index:9600',
  ].join(';');

  document.body.appendChild(popover);

  const dismiss = (ev: MouseEvent) => {
    if (!popover.contains(ev.target as Node) && ev.target !== anchorBtn) {
      if (popover.parentNode) popover.parentNode.removeChild(popover);
      document.removeEventListener('click', dismiss);
    }
  };
  setTimeout(() => document.addEventListener('click', dismiss), 50);
  setTimeout(() => {
    if (popover.parentNode) popover.parentNode.removeChild(popover);
    document.removeEventListener('click', dismiss);
  }, 8000);
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export function renderThreadRecallBanner({ topic, summary }: { topic: string; summary: string }) {
  const existing = document.getElementById('ai-thread-recall');
  if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

  const banner = document.createElement('div');
  banner.id        = 'ai-thread-recall';
  banner.className = 'ai-thread-recall-banner';
  banner.setAttribute('role', 'status');
  banner.setAttribute('aria-live', 'polite');

  const topicText   = escapeHtml(topic || 'your previous conversation');
  const summaryText = summary ? escapeHtml(summary) : '';

  banner.innerHTML = [
    '<div class="ai-thread-recall-inner">',
      '<span class="ai-thread-recall-icon" aria-hidden="true">🔗</span>',
      '<div class="ai-thread-recall-text">',
        '<span class="ai-thread-recall-label">Continuing: <strong>' + topicText + '</strong></span>',
        summaryText ? '<span class="ai-thread-recall-summary">' + summaryText + '</span>' : '',
      '</div>',
      '<button class="ai-thread-recall-dismiss" aria-label="Dismiss thread recall" title="Dismiss">✕</button>',
    '</div>',
  ].join('');

  banner.querySelector('.ai-thread-recall-dismiss')!.addEventListener('click', () => {
    banner.style.transition = 'opacity 0.2s, transform 0.2s';
    banner.style.opacity    = '0';
    banner.style.transform  = 'translateY(-4px)';
    setTimeout(() => { if (banner.parentNode) banner.parentNode.removeChild(banner); }, 220);
  });

  const modeBar = document.getElementById('ai-mode-bar');
  const docChip = document.getElementById('ai-doc-chip');
  const target  = modeBar || docChip;
  const drawer  = document.getElementById('ai-drawer');

  if (target && target.parentNode) {
    target.parentNode.insertBefore(banner, target);
  } else if (drawer) {
    drawer.appendChild(banner);
  }
}

export function renderTransparencyPopover(contextUsed: any, bubbleEl: HTMLElement) {
  if (!contextUsed || !bubbleEl) return;
  if (!contextUsed.facts?.length && !contextUsed.recall?.length && !contextUsed.style) return;

  const bodyWrap = bubbleEl.querySelector('.ai-msg-body-wrap');
  if (!bodyWrap) return;

  if (bodyWrap.querySelector('.ai-transparency-btn')) return;

  const btn = document.createElement('button');
  btn.className   = 'ai-transparency-btn';
  btn.textContent = 'ℹ';
  btn.setAttribute('aria-label', 'Why did Kili say this?');
  btn.setAttribute('title',      'See what context Kili used');
  btn.type        = 'button';

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    _toggleTransparencyPopover(btn, contextUsed);
  });

  const feedbackRow = bodyWrap.querySelector('.ai-msg-feedback');
  if (feedbackRow) {
    feedbackRow.parentNode!.insertBefore(btn, feedbackRow.nextSibling);
  } else {
    bodyWrap.appendChild(btn);
  }
}
